import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getGluceraAccessToken,
  getGluceraUserInfo,
  clearGluceraStorage,
} from "../utils/storage";
import { refreshGluceraAuthTokens } from "../utils/authTokens";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import { jwtDecode } from "jwt-decode";
import {
  AUTH_SESSION_UPDATED,
  hasAuthSession,
  isAccessExpiredOrSoon,
} from "@/services/tokenRefresh";

export const useGluceraAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    clearGluceraStorage();

    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("glucera_refresh_token");

    localStorage.removeItem("userProfile");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("onboardingComplete");
    localStorage.removeItem("glucera_onboarding_complete");

    sessionStorage.clear();

    localStorage.removeItem("tenantId");
    localStorage.removeItem("assessment_data");
    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("cached_meals");
    localStorage.removeItem("user_preferences");
    localStorage.removeItem("glucera_document_id");

    localStorage.removeItem("preferencesData");

    setIsAuthenticated(false);
    setUser(null);
    setLoading(false);

    navigate("/glucera" + GluceraNavigationRoutes.ONBOARDING_WELCOME, {
      replace: true,
    });
  }, [navigate]);

  const markAuthenticated = useCallback(() => {
    const userInfo = getGluceraUserInfo();
    setIsAuthenticated(true);
    setUser(userInfo);
    setLoading(false);
  }, []);

  const checkAuth = useCallback(async () => {
    if (!hasAuthSession()) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return false;
    }

    const token = getGluceraAccessToken();
    if (!token) {
      // Refresh token exists but access token missing — wait for ProtectedRoute/login
      // to refresh; do not call token-refresh on public/welcome screens.
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return false;
    }

    try {
      const decoded: { exp: number } = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp > currentTime + 60) {
        markAuthenticated();
        return true;
      }
    } catch {
      // invalid token — fall through
    }

    setIsAuthenticated(false);
    setUser(null);
    setLoading(false);
    return false;
  }, [markAuthenticated]);

  useEffect(() => {
    void checkAuth();

    const onSessionUpdated = () => {
      markAuthenticated();
    };
    window.addEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "glucera_access_token" && !e.newValue) {
        sessionStorage.clear();
        localStorage.removeItem("tenantId");
        localStorage.removeItem("assessment_data");
        localStorage.removeItem("onboarding_completed");

        setIsAuthenticated(false);
        setUser(null);
        navigate("/glucera" + GluceraNavigationRoutes.ONBOARDING_WELCOME, {
          replace: true,
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const onVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        isAuthenticated &&
        hasAuthSession() &&
        isAccessExpiredOrSoon()
      ) {
        void refreshGluceraAuthTokens().then(tokens => {
          if (tokens?.access) {
            markAuthenticated();
          }
        });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [navigate, checkAuth, markAuthenticated]);

  return {
    isAuthenticated,
    user,
    loading,
    logout,
    checkAuth,
  };
};
